import React from 'react';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputLocation from 'components/inputs/input-location/InputLocation';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { t } from 'utils/language';
import { LONG_STRING_MAX_LENGTH, isPhoneNumber, isRequired, maxLength } from 'utils/validators';
import { sortAndUniqueItems } from 'utils/helper/common';

import './address-settings-form.scss';

import { LocationSuggestionCityOrCountry, MixedSelectDropdownItem, OrganizationSettingsForm, TextEntryKeyboardEvent } from 'types';

type handlePhoneNumberEditFuncType = (
  value: string,
  field: string,
  countries?: Array<MixedSelectDropdownItem>
) => void;

type Props = {
  values: OrganizationSettingsForm['billingAddress'],
  canEdit: boolean,
  isBrand?: boolean,
  optional?: boolean,
  organizationType?: string,
  hidePhone?: boolean,
  labelType?: 'highlighted' | 'normal',
  direction?: 'column' | 'row',
  labelSize?: 'fixed' | 'collapsed' | 'custom',
  countries?: Array<MixedSelectDropdownItem> | null,
  onLocationInput: (event: TextEntryKeyboardEvent, location: LocationSuggestionCityOrCountry) => void,
  onTextInput: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void,
  handlePhoneNumberEdit?: handlePhoneNumberEditFuncType | null
}

const AddressSettingsForm = ({
  values,
  direction,
  labelType,
  labelSize,
  onTextInput,
  onLocationInput,
  canEdit,
  isBrand,
  organizationType,
  countries,
  hidePhone,
  handlePhoneNumberEdit,
  optional
}: Props) => {
  const width = direction === 'column' ? 'responsive' : 'fixed';

  const isRequiredMasked = (value) => {
    if (optional) {
      return {
        valid: true,
        message: ''
      };
    }

    return isRequired(value);
  };

  const handleFocus = (event) => event.target.select();

  return (
    <React.Fragment>
      {
        isBrand && (
          <FormField
            direction={direction}
            key="address-settings-form__legal-brand-name"
            label={t(`address_details_legal_${organizationType}_name`)}
            labelType={labelType}
            labelSize={labelSize}
            fontSize="small"
            showRequiredMark
            showValidationIcon={!optional}
            validators={[
              isRequiredMasked(values.legalName),
              maxLength(values.legalName, LONG_STRING_MAX_LENGTH)
            ]}
          >
            <Input
              type="text"
              name="legalName"
              width={width}
              value={values.legalName}
              disabled={!canEdit}
              onChange={onTextInput}
              onFocus={handleFocus}
            />
          </FormField>
        )
      }

      <FormField
        direction={direction}
        key="address-settings-form__address-line-1"
        label={t('address_details_address_line_1')}
        labelType={labelType}
        labelSize={labelSize}
        fontSize="small"
        showRequiredMark
        showValidationIcon={!optional}
        validators={[
          isRequiredMasked(values.addressLine1),
          maxLength(values.addressLine1, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <Input
          type="text"
          name="addressLine1"
          width={width}
          value={values.addressLine1}
          disabled={!canEdit}
          onChange={onTextInput}
          onFocus={handleFocus}
        />
      </FormField>

      <FormField
        direction={direction}
        key="address-settings-form__city"
        label={t('address_details_city')}
        labelType={labelType}
        labelSize={labelSize}
        fontSize="small"
        showRequiredMark
        showValidationIcon={!optional}
        validators={[
          isRequiredMasked(values.city),
          maxLength(values.city, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <InputLocation
          allowsFreeEntry
          key={JSON.stringify(values.city)}
          id="address-city"
          name="city"
          value={values.city}
          valueType="city"
          suggestionType="fullName"
          disabled={!canEdit}
          onChange={onLocationInput}
        />
      </FormField>

      <FormField
        direction={direction}
        key="address-settings-form__state"
        label={t('address_details_state')}
        labelType={labelType}
        labelSize={labelSize}
        fontSize="small"
        showValidationIcon={!optional}
        validators={[
          maxLength(values.state, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <Input
          type="text"
          name="state"
          width={width}
          value={values.state}
          disabled={!canEdit}
          onChange={onTextInput}
          onFocus={handleFocus}
        />
      </FormField>

      <FormField
        direction={direction}
        key="address-settings-form__postal-code"
        label={t('address_details_postal_code')}
        labelType={labelType}
        labelSize={labelSize}
        fontSize="small"
        showRequiredMark
        showValidationIcon={!optional}
        validators={[
          isRequiredMasked(values.postalCode),
          maxLength(values.postalCode, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <Input
          type="text"
          name="postalCode"
          width={width}
          value={values.postalCode}
          disabled={!canEdit}
          onChange={onTextInput}
          onFocus={handleFocus}
        />
      </FormField>

      <FormField
        direction={direction}
        key="address-settings-form__country"
        label={t('address_details_country')}
        labelType={labelType}
        labelSize={labelSize}
        fontSize="small"
        showRequiredMark
        showValidationIcon={!optional}
        validators={[
          isRequiredMasked(values.country),
          maxLength(values.country, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <InputLocation
          key={JSON.stringify(values.country)}
          id="address-country"
          name="country"
          value={values.country}
          valueType="country"
          suggestionType="country"
          disabled={!canEdit}
          onChange={onLocationInput}
        />
      </FormField>

      {
        !isBrand && !hidePhone && (
          <FormField
            direction={direction}
            key="address-settings-form__phone"
            label={t('address_details_phone')}
            labelType={labelType}
            labelSize={labelSize}
            fontSize="small"
            validators={
              values.phone.number ? [
                isPhoneNumber(`${values.phone.countryCode}${values.phone.number}`, 'This is not a valid phone number.')] : []}
          >
            <div
              className={canEdit ? 'address-settings-form__phone-number-edit' : ''}
            >
              {
                canEdit && (
                  <MixedSelectDropdown
                    name="address-settings-phone-number"
                    items={sortAndUniqueItems(countries || [])}
                    onItemsToggle={(items, data, selectedCountry) => {
                      if (handlePhoneNumberEdit && selectedCountry?.value) {
                        handlePhoneNumberEdit(selectedCountry?.value, 'countryCode', items);
                      }
                    }}
                    withSearchable
                    className="address-settings-form__country-select"
                  />
                )
              }
              <Input
                type="text"
                name="phone"
                className="address-settings-form__phone-input"
                value={canEdit ? values.phone.number : values.phone.originalNumber}
                disabled={!canEdit}
                onChange={(event) => {
                  if (handlePhoneNumberEdit) {
                    handlePhoneNumberEdit(event.currentTarget.value, 'number');
                  }
                }}
                onFocus={handleFocus}
              />
            </div>
          </FormField>
        )
      }
    </React.Fragment>
  );
};

export default AddressSettingsForm;
